import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { FundAsset } from "@/types";

export function useFundAssets(fundId?: string) {
  return useQuery({
    queryKey: ["fund_assets", fundId ?? "all"],
    queryFn: async () => {
      const params = fundId ? { fund_id: fundId } : undefined;
      const { data } = await apiClient.get<FundAsset[]>("/fund-assets", { params });
      return data;
    },
  });
}

export const useAllFundAssets = () => useFundAssets(undefined);

export function useCreateFundAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Omit<FundAsset, "id" | "created_at" | "updated_at">) => {
      const { data } = await apiClient.post<FundAsset>("/fund-assets", payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fund_assets"] }),
  });
}

export function useUpdateFundAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<FundAsset> & { id: string }) => {
      const { data } = await apiClient.put<FundAsset>(`/fund-assets/${id}`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fund_assets"] }),
  });
}

export function useDeleteFundAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/fund-assets/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fund_assets"] }),
  });
}
